import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../lib/org';
import * as orgApi from '../api/organizations';
import { useToast } from '../components/toast';
import { ErrorAlert, Field, TextareaField, SelectField } from '../components/ui';
import { cn, ui } from '../lib/ui';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

export default function CreateOrg() {
  const { refresh } = useOrg();
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState({
    name: '',
    slug: '',
    description: '',
    billingEmail: '',
    visibility: 'invite_only',
  });
  const [autoSlug, setAutoSlug] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function toSlug(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
  }

  function handleNameChange(e) {
    const name = e.target.value;
    setForm(prev => ({
      ...prev,
      name,
      ...(autoSlug ? { slug: toSlug(name) } : {}),
    }));
  }

  function handleSlugChange(e) {
    setAutoSlug(false);
    setForm(prev => ({ ...prev, slug: e.target.value }));
  }

  function handleChange(field) {
    return e => setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  const slugValid = form.slug === '' || SLUG_RE.test(form.slug);
  const canSubmit = form.name.trim() && form.slug && slugValid && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug,
        description: form.description.trim(),
        visibility: form.visibility,
      };
      if (form.billingEmail.trim()) {
        payload.billingEmail = form.billingEmail.trim();
      }
      const data = await orgApi.createOrganization(payload);
      toast.success(`Organization "${data.organization.name}" created`);
      try {
        localStorage.setItem('cap.activeOrgId', data.organization.orgId);
      } catch {
        // localStorage unavailable
      }
      await refresh();
      navigate('/org/setup');
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <h1 className={ui.h1}>Create Organization</h1>
      <div className={cn(ui.card, 'max-w-xl')}>
        <form onSubmit={handleSubmit}>
          {error && <ErrorAlert error={error} />}

          <Field
            label="Organization name"
            value={form.name}
            onChange={handleNameChange}
            required
            maxLength={200}
            placeholder="My Organization"
          />

          <Field
            label="URL slug"
            value={form.slug}
            onChange={handleSlugChange}
            required
            maxLength={64}
            pattern="[a-z0-9][a-z0-9-]{1,62}[a-z0-9]"
            hint={
              slugValid
                ? 'Lowercase letters, numbers, and hyphens (3-64 characters).'
                : 'Invalid slug. Use lowercase letters, numbers, and hyphens (3-64 characters).'
            }
            placeholder="my-organization"
          />

          <Field
            label="Billing email"
            type="email"
            value={form.billingEmail}
            onChange={handleChange('billingEmail')}
            maxLength={200}
            placeholder="billing@example.com"
            hint="Used for invoices and billing notifications. Defaults to your account email if left blank."
          />

          <TextareaField
            label="Description"
            value={form.description}
            onChange={handleChange('description')}
            maxLength={1000}
            rows={3}
            placeholder="A brief description of the organization."
          />

          <SelectField
            label="Visibility"
            value={form.visibility}
            onChange={handleChange('visibility')}
            hint="Public organizations appear in the directory. Invite-only organizations are visible only to members."
          >
            <option value="invite_only">Invite only</option>
            <option value="public">Public</option>
            <option value="import_only">Import only</option>
          </SelectField>

          <div className="mt-4">
            <button type="submit" className={ui.btnPrimary} disabled={!canSubmit}>
              {submitting ? 'Creating...' : 'Create organization'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
